import { CapacityMember, ProductOp, SavePlan } from 'capacity/api/capacityGateway'
import { BLAST_RADIUS, CONFIG, LIMIT, SAVE } from 'capacity/copy'
import {
    CapacityDetails,
    CountingType,
    countingTypeToWire,
    fromLimitState,
    LimitState,
    limitStatesEqual,
    parseLimitInput,
} from 'capacity/types'
import { addSeparators } from 'utils'
import { accountWideAmount, CapacityDraft } from './capacityDraft'

export interface CapacityFormValues {
    name: string
    limit: string
    countingType: CountingType
}

export interface CapacityBaseline {
    record: CapacityDetails | null
    name: string
    limit: LimitState
    countingType: CountingType
}

export interface SavePlanInput {
    accountSlug: string
    capacityId: string | null
    baseline: CapacityBaseline
    values: CapacityFormValues
    draft: CapacityDraft
    members: CapacityMember[]
}

export const limitText = (state: LimitState): string => {
    switch (state.kind) {
        case 'noLimit':
            return LIMIT.noLimit
        case 'closed':
            return LIMIT.closed
        default:
            return addSeparators(state.value)
    }
}

export const parsedLimit = (values: CapacityFormValues): LimitState | 'invalid' => parseLimitInput(values.limit)

const trimmedName = (values: CapacityFormValues) => values.name.trim()

export const configChanged = (baseline: CapacityBaseline, values: CapacityFormValues): boolean => {
    const limit = parsedLimit(values)
    return trimmedName(values) !== baseline.name || limit === 'invalid' || !limitStatesEqual(limit, baseline.limit)
}

export function productOps(draft: CapacityDraft, members: CapacityMember[]): ProductOp[] {
    const byId = new Map(members.map((member) => [member.id, member]))
    const ops: ProductOp[] = []
    for (const add of Object.values(draft.adds)) {
        ops.push({ kind: 'add', productId: add.member.id, label: add.member.name, amountUsed: add.amountUsed })
    }
    for (const productId of Object.keys(draft.removals)) {
        const member = byId.get(productId)
        ops.push({
            kind: 'remove',
            productId,
            label: member?.name ?? productId,
            expectedLinks: member?.links,
        })
    }
    for (const [productId, amount] of Object.entries(draft.amountChanges)) {
        if (draft.removals[productId]) {
            continue
        }
        const member = byId.get(productId)
        ops.push({
            kind: 'setAmount',
            productId,
            label: member?.name ?? productId,
            amountUsed: amount,
            expectedLinks: member?.links,
        })
    }
    return ops
}

export function buildSavePlan(input: SavePlanInput): SavePlan {
    const { accountSlug, capacityId, baseline, values, draft, members } = input
    const limit = parsedLimit(values)
    const limitValue = fromLimitState(limit === 'invalid' ? baseline.limit : limit)
    const edited = {
        name: trimmedName(values),
        defaultAvailability: limitValue,
        reservationPeriodType: baseline.record
            ? baseline.record.reservationPeriodType
            : countingTypeToWire(values.countingType),
    }
    let capacity: SavePlan['capacity'] = null
    if (!baseline.record) {
        capacity = { kind: 'create', body: { account: accountSlug, ...edited } }
    } else if (configChanged(baseline, values)) {
        const { availabilityRules: _rules, ...record } = baseline.record
        capacity = { kind: 'update', body: { ...record, ...edited } }
    }
    return { capacityId, capacity, productOps: productOps(draft, members) }
}

export function saveParts(input: Omit<SavePlanInput, 'accountSlug' | 'capacityId'>): string[] {
    const { baseline, values, draft, members } = input
    const parts: string[] = []
    if (baseline.record && trimmedName(values) !== baseline.name) {
        parts.push(SAVE.parts.rename)
    }
    const limit = parsedLimit(values)
    if (limit !== 'invalid' && !limitStatesEqual(limit, baseline.limit) && baseline.record) {
        parts.push(SAVE.parts.limit(limitText(baseline.limit), limitText(limit)))
    }
    const adds = Object.keys(draft.adds).length
    if (adds > 0) {
        parts.push(SAVE.parts.add(adds))
    }
    const removals = Object.keys(draft.removals).length
    if (removals > 0) {
        parts.push(SAVE.parts.remove(removals))
    }
    const changes = Object.entries(draft.amountChanges).filter(([productId]) => !draft.removals[productId])
    if (changes.length === 1) {
        const [productId, amount] = changes[0]
        const member = members.find((entry) => entry.id === productId)
        const from = member ? accountWideAmount(member) ?? 0 : 0
        parts.push(SAVE.parts.uses(member?.name ?? productId, from, amount))
    } else if (changes.length > 1) {
        parts.push(SAVE.parts.usesMany(changes.length))
    }
    return parts
}

export const saveLabel = (parts: string[], isNew: boolean): string => (isNew ? SAVE.create : SAVE.label(parts))

export function saveDisabledReason(input: Omit<SavePlanInput, 'accountSlug' | 'capacityId'>): string | null {
    const { baseline, values, draft } = input
    if (trimmedName(values) === '') {
        return SAVE.nameMissing
    }
    if (parsedLimit(values) === 'invalid') {
        return CONFIG.limitInvalid
    }
    const anyChange =
        !baseline.record ||
        configChanged(baseline, values) ||
        Object.keys(draft.adds).length + Object.keys(draft.removals).length + Object.keys(draft.amountChanges).length >
            0
    return anyChange ? null : SAVE.nothingChanged
}

export const confirmText = (parts: string[], isNew: boolean, name: string): string => {
    const all = isNew ? [SAVE.parts.create(name), ...parts] : parts
    const sentence = all.join('; ')
    const changes = sentence ? `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.` : ''
    return [changes, BLAST_RADIUS].filter(Boolean).join(' ')
}
